import React, { useEffect, useMemo, useRef, useState } from "react";

import { KeyboardArrowDown as KeyboardArrowDownIcon } from "@mui/icons-material";
import { MenuItem, Tooltip, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ShipeeziGrid, { Column } from "Components/AppGrid/shipeezi-new-grid";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import { Input } from "../../../../../components/Form";
import { Color } from "../../../../../components/Form/Button";
import FilterMenu from "../../../../../components/Form/FilterMenu";
import InputDateRange from "../../../../../components/Form/Input/DateRange";
import BadgeStatus from "../../../../../components/Generic/BadgeStatus";
import BadgeSubStatus from "../../../../../components/Generic/BadgeSubStatus";
import Breadcrumb from "../../../../../components/Generic/Breadcrump";
import SubHeader from "../../../../../components/Generic/SubHeader";
import { PRIVATE_ROUTES } from "../../../../../constants";
import { useAppState } from "../../../../../context/StateContext";
import { Customer } from "../../../../../models/customer";
import { Job } from "../../../../../models/job";
import { JobMode } from "../../../../../models/job.mode";
import { JobType } from "../../../../../models/jobType";
import { ReportGroup } from "../../../../../models/report.group";
import { Filters, Searches, Sort } from "../../../../../models/shipeezi.table";
import { Statuses } from "../../../../../models/statuses";
import JobReportService from "../../../../../services/job.report.service";
import JobTypeService from "../../../../../services/job.type.service";
import ModeJobService from "../../../../../services/mode.job.service";
import StatusesService from "../../../../../services/statuses.service";
import {
  ReportGroup as EReportGroup,
  ReportName,
  ReportSubGroup,
  UsedByStatuses,
} from "../../../../../util/enums";
import { getFullNameCustomer } from "../../../../../util/getFullNameCustomer";
import { handleSort } from "../../../../../util/handleSort";
import GenerateReportModal, { GenerateReportModalHandler } from "../../Shared/GenerateReportModal";
import { LocationsStatusReportsScreen } from "./LocationsStatusReportsScreen";
import { OrderAndTaskUsageReportScreen } from "./ManagementReportScreen";
import { MovementsReportsScreen } from "./MovementsReportsScreen";
import {
  EmptyDiv,
  ExportButton,
  ExportMenu,
  GridDiv,
  MainDiv,
  PDFIconButton,
  ReportTitle,
} from "./styled";
import { ResourceObjectTypeEnum } from "Util/enums/accessRule";
import { CostingReportScreen } from "./CostingReportsScreen";
import { SurveyReportScreen } from "./SurveyReportScreen";
import { isMoving } from "Util/validation";
import { OrderReportScreen } from "./OrderReportScreen";
import { useQuickOrderFeature } from "Hooks/useQuickOrderFeature";
import {
  shouldBypassReportPermission,
  shouldRedirectDisabledOrderReport,
} from "./reportMaintenanceGuards";

const today = new Date();

type ReportProps = {
  reportGroup?: ReportGroup;
  name?: string;
  checklistId?: number;
  reportId?: number;
};

const ReportMaintenance = () => {
  const { t: translate } = useTranslation();

  const history = useHistory();

  const { state } = useLocation<ReportProps>();

  const { reportGroup, name, checklistId, reportId } = state || {};

  const { currentUser, userPermissions } = useAppState();
  const { managementOrderReportEnabled, isLoadingQuickOrderFlags } = useQuickOrderFeature();

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const [isDateRange, setIsDateRange] = useState<boolean>(true);

  const [jobModes, setJobModes] = useState<JobMode[]>();
  const [jobTypes, setJobTypes] = useState<JobType[]>();

  const [filters, setFilters] = useState<Filters>();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [sorting, setSorting] = useState<Array<string[]>>();
  const [selecteds, setSelecteds] = useState<number[]>([]);
  const [searches, setSearches] = useState<Searches>([]);

  const [statuses, setStatuses] = useState<Statuses[]>([]);

  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [date, setDate] = useState<string | undefined>(format(new Date(), "yyyy-MM-dd"));

  const isQuote = reportGroup?.group === EReportGroup.Business;
  const isOrder = reportGroup?.group === EReportGroup.Operations;
  const isManagementReport = reportGroup?.group === EReportGroup.Management;
  const isQuickOrderReport =
    name?.toLowerCase() === ReportName.ORDER_REPORT.toLowerCase();
  const shouldBypassReportPermissionCheck = shouldBypassReportPermission({
    isManagementReport,
    isOrderReport: isQuickOrderReport,
    isLoadingQuickOrderFlags,
    managementOrderReportEnabled,
  });

  const modalRef = useRef<GenerateReportModalHandler>(null);

  const branchIds = useMemo(() => {
    return currentUser?.branches?.map((branch) => branch.id);
  }, [currentUser]);

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      if (shouldBypassReportPermissionCheck) {
        return;
      }

      const reports = userPermissions
        ?.map((permission) => {
          if (
            permission?.objectType === ResourceObjectTypeEnum.FUNCTION &&
            permission?.objectDetails?.objectData?.includes("reports")
          ) {
            return permission;
          }
        })
        ?.filter((permission) => !!permission);

      const isHavePermissionToViewReport = reports?.some(
        (report) => report?.objectDetails?.id === (state?.reportId ?? state?.checklistId)
      );
      if (!isHavePermissionToViewReport) {
        history.push(PRIVATE_ROUTES.ANALYTICS);
      }
    }
  }, [userPermissions, state, shouldBypassReportPermissionCheck, history]);

  useEffect(() => {
    if (
      shouldRedirectDisabledOrderReport({
        isOrderReport: isQuickOrderReport,
        isLoadingQuickOrderFlags,
        managementOrderReportEnabled,
      })
    ) {
      history.push(PRIVATE_ROUTES.ANALYTICS);
    }
  }, [isQuickOrderReport, isLoadingQuickOrderFlags, managementOrderReportEnabled, history]);

  useEffect(() => {
    if (!state) history.push("/");
  }, []);

  useEffect(() => {
    if (searches && searches?.length > 0) {
      setStartDate("");
      setEndDate("");
      setDate("");
    }
  }, [searches]);

  useQuery(
    ["jobType.list"],
    () =>
      JobTypeService.getAll({
        companyId: currentUser?.companyId,
        sortBy: JSON.stringify([["name", "asc"]]),
      }),
    {
      enabled: !!((isQuote || isOrder) && currentUser?.companyId),
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) setJobTypes(response.data);
      },
    }
  );

  useQuery(
    ["statuses.list"],
    () =>
      StatusesService.getAll({
        usedBy: isQuote ? UsedByStatuses.QUOTES : UsedByStatuses.ORDERS,
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !!((isQuote || isOrder) && currentUser?.companyId),
      onSuccess: (response) => {
        if (response && response.data && response.data.length > 0) {
          setStatuses(response.data);
        }
      },
    }
  );

  useQuery(
    ["mode.list"],
    () =>
      ModeJobService.getAll({
        companyId: currentUser?.companyId,
      }),
    {
      enabled: !!((isQuote || isOrder) && currentUser?.companyId),
      onSuccess: (response) => {
        if (response && response.data) setJobModes(response.data);
      },
    }
  );

  const { isFetching: loadingQuotes } = useQuery(
    [
      "job.report.list",
      page,
      pageSize,
      sorting,
      filters,
      date,
      startDate,
      endDate,
      isDateRange,
      searches,
    ],
    () =>
      JobReportService.getAll({
        companyId: currentUser?.companyId,
        page,
        pageSize,
        sortBy:
          sorting && sorting?.length > 0
            ? JSON.stringify(sorting)
            : JSON.stringify([["updatedAt", "DESC"]]),
        userId: currentUser?.id,
        date: !isDateRange ? date || "" : "",
        startDate: isDateRange ? startDate || "" : "",
        endDate: isDateRange ? endDate || "" : "",
        reportGroupId: reportGroup?.id || "",
        reportId: reportId || "",
        checklistId: checklistId || "",
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        include: JSON.stringify([
          { association: "branchFrom", required: false, where: { id: [...branchIds!, null] } },
          { association: "toBranch", required: false, where: { id: [...branchIds!, null] } },
        ]),
        ...filters,
      }),
    {
      enabled: !!(currentUser?.id && (isQuote || isOrder)),
      refetchOnWindowFocus: false,

      onSuccess: (response: any) => {
        if (response && response?.data) {
          setSelecteds([]);
          setJobs(response.data);
        }
      },
    }
  );

  useQuery(
    ["job.report.count", filters, date, startDate, endDate, isDateRange, searches],
    () =>
      JobReportService.count({
        companyId: currentUser?.companyId,
        userId: currentUser?.id,
        date: !isDateRange ? date || "" : "",
        startDate: isDateRange ? startDate || "" : "",
        endDate: isDateRange ? endDate || "" : "",
        reportGroupId: reportGroup?.id || "",
        reportId: reportId || "",
        checklistId: checklistId || "",
        searchByOr: searches && searches?.length > 0 ? JSON.stringify(searches) : "",
        include: JSON.stringify([
          { association: "branchFrom", required: false, where: { id: [...branchIds!, null] } },
          { association: "toBranch", required: false, where: { id: [...branchIds!, null] } },
        ]),
        ...filters,
      }),
    {
      enabled: !!(currentUser?.id && (isQuote || isOrder)),
      onSuccess: (response) => {
        setTotal(response?.data || 0);
      },
    }
  );

  const handleCurrentPage = (numPage: number) => {
    setPage(numPage - 1);
  };

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const getJobModes = () => {
    const jobModesExtracted: string[] = [];

    if (jobModes && jobModes.length > 0) {
      jobModes?.forEach((jobMode) => {
        if (jobMode && jobMode?.name) {
          jobModesExtracted.push(jobMode?.name);
        }
      });
    }

    return jobModesExtracted;
  };

  const getJobTypes = () => {
    const jobTypesExtracted: string[] = [];

    if (jobTypes && jobTypes.length > 0) {
      jobTypes?.forEach((jobType) => {
        if (jobType && jobType?.name) {
          jobTypesExtracted.push(jobType?.name);
        }
      });
    }

    return jobTypesExtracted;
  };

  const getStatuses = () => {
    const statusesExtracted: string[] = [];

    if (statuses && statuses.length > 0) {
      statuses?.forEach((status) => {
        if (status && status?.name) {
          statusesExtracted.push(status?.name);
        }
      });
    }

    return statusesExtracted;
  };

  const handlePDF = (id: number, job: Job) => {
    if (job && job?.tasks && job?.tasks?.length > 0) {
      return (
        <Tooltip title={translate("pdf")} placement="top">
          <div>
            <PDFIconButton
              onClick={() => {
                modalRef.current?.handleOpenModal([id], true);
              }}
              color="#ff5630"
              size={18}
            />
          </div>
        </Tooltip>
      );
    }

    return null;
  };

  const renderReportName = () => {
    switch (reportGroup?.group) {
      case EReportGroup.Management:
        return null;

      case EReportGroup.Warehousing:
        return null;

      default:
        if (name) {
          return <ReportTitle>{name}</ReportTitle>;
        }

        return null;
    }
  };

  const getStartDate = () => {
    if (startDate === undefined) return new Date(today.getFullYear(), today.getMonth(), 1);
    if (startDate) return new Date(startDate);

    return undefined;
  };

  const getEndDate = () => {
    if (endDate === undefined) return today;
    if (endDate) return new Date(endDate);

    return undefined;
  };

  const renderInputDateRange = () => {
    if (isDateRange) {
      return (
        <InputDateRange
          pStartDate={getStartDate()}
          pEndDate={getEndDate()}
          onChange={(range) => {
            setStartDate(range?.startDate ? format(range?.startDate, "yyyy-MM-dd") : undefined);
            setEndDate(range?.endDate ? format(range?.endDate, "yyyy-MM-dd") : undefined);
          }}
        />
      );
    }

    return (
      <Input
        type="date"
        value={date}
        onChange={(event) => {
          const date = event.target.value;

          setDate(date);
        }}
      />
    );
  };

  const renderSelectInputDateRange = () => {
    return <FilterMenu isDateRange={isDateRange} setIsDateRange={setIsDateRange} />;
  };

  const renderEmptyDiv = () => {
    return (
      <EmptyDiv>
        <Typography
          fontFamily="Manrope"
          fontWeight={400}
          fontSize="16px"
          color="#9CA6AF"
          lineHeight="18px"
          marginBottom="28px"
        >
          {translate("itFeelsEmptyRightNow")}
        </Typography>
      </EmptyDiv>
    );
  };

  const renderExportButton = () => {
    if (selecteds && selecteds?.length > 0) {
      return (
        <>
          <ExportButton
            customColor={Color.INFO}
            endIcon={<KeyboardArrowDownIcon htmlColor="#667085" />}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
          >
            {translate("exportAs")}
          </ExportButton>
          <ExportMenu
            anchorEl={menuAnchor}
            keepMounted
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            <MenuItem
              onClick={() => {
                modalRef.current?.handleOpenModal(selecteds, true);
                setMenuAnchor(null);
              }}
            >
              {translate("pdf")}
            </MenuItem>
          </ExportMenu>
        </>
      );
    }

    return null;
  };

  const getColumns = () => {
    const columns: Column[] = [
      { name: "reference", title: translate("reference") },
      {
        name: "customer",
        title: translate("customer"),
        hideSearch: true,
        hideSort: true,
        formatText: (customer: Customer) => getFullNameCustomer(customer),
      },
      {
        name: "type",
        hideSearch: true,
        title: translate("type"),
      },
      {
        name: "createdAt",
        hideSearch: true,
        title: translate("date"),
        formatText: (date: string) => format(new Date(date), "dd/MM/yyyy"),
      },
      {
        name: "mode",
        hideSearch: true,
        title: translate("mode"),
        formatComponent: (value: string) =>
          value ? translate(value.toLowerCase()) : "",
      },
    ];

    if (isQuote) {
      columns.push({
        name: "subStatus",
        title: translate("status"),
        hideSearch: true,
        formatComponent: (value: string) => <BadgeSubStatus value={value} />,
      });
    } else if (isOrder) {
      columns.push({
        name: "status",
        hideSearch: true,
        title: translate("status"),
        formatComponent: (value: string) => <BadgeStatus value={value} />,
      });
    }

    return columns;
  };

  const chooseGridBySubgroup = () => {
    if (reportGroup?.group === EReportGroup.Management) {
      if (
        name?.toLowerCase() === ReportName.ORDER_AND_TASK_USAGE_REPORT.toLowerCase()
      ) {
        return <OrderAndTaskUsageReportScreen />;
      }
      if (name?.toLowerCase() === ReportName.ORDER_REPORT.toLowerCase()) {
        if (isLoadingQuickOrderFlags || !managementOrderReportEnabled) {
          return <></>;
        }
        return <OrderReportScreen />;
      }
      if (name?.toLowerCase() === ReportName.COSTING_REPORT.toLowerCase()) {
        return <CostingReportScreen />;
      }
      return <></>;
    }

    switch (reportGroup?.subGroup) {
      case ReportSubGroup.Orders:
        return (
          <ShipeeziGrid
            loadingData={loadingQuotes}
            emptyData={() => renderEmptyDiv()}
            onSorting={(sorts: Sort[]) => setSorting(handleSort(sorts))}
            data={jobs}
            onChangePageSize={handlePageSize}
            onNextPage={handleCurrentPage}
            onPreviousPage={handleCurrentPage}
            onGoToPage={handleCurrentPage}
            columns={getColumns()}
            totalItems={total}
            pageSize={pageSize}
            pageSizeList={[20, 40, 60, 100]}
            currentPage={page}
            positionActionsColumn="right"
            selecteds={selecteds}
            handleSelect={setSelecteds}
            generalActions={{
              orderBy: {
                right: ["export"],
                left: ["selectDateRange", "dateRange", "handleSearch", "handleFilterBy"],
              },
              export: renderExportButton(),
              selectDateRange: renderSelectInputDateRange(),
              dateRange: renderInputDateRange(),
              handleSearch: {
                onSearch: (filters) => handleSearch(filters),
              },
              handleFilterBy: {
                categories: [
                  {
                    name: translate("mode"),
                    column: "mode",
                    options: getJobModes(),
                  },
                  {
                    name: translate("type"),
                    column: "type",
                    options: getJobTypes(),
                  },
                  {
                    name: translate("status"),
                    column: isQuote ? "sub_status" : "status",
                    options: getStatuses(),
                  },
                ],
                onChange: handleFilter,
              },
            }}
            rowActionsExtra={[
              {
                title: translate("pdf"),
                key: "pdf",
                buttonAndAction: handlePDF,
              },
            ]}
          />
        );
      case ReportSubGroup.Warehouses:
        switch (name) {
          case ReportName.LOCATION_STATUS:
            return <LocationsStatusReportsScreen />;
          case ReportName.MOVEMENTS:
            return <MovementsReportsScreen />;
          default:
            return <></>;
        }
      case ReportSubGroup.Quotes:
        switch (name) {
          case ReportName.SURVEY_REPORT:
            return <SurveyReportScreen reportId={reportId} />;
          default:
            return <></>;
        }
      default:
        return <></>;
    }
  };

  const getBreadCrumbItems = (reportGroup?: ReportGroup) => {
    switch (reportGroup?.group) {
      case EReportGroup.Warehousing:
        return [
          {
            title: translate("reports"),
            link: {
              url: PRIVATE_ROUTES.ANALYTICS,
            },
          },
          {
            title: reportGroup?.group || "",
          },
          {
            title: reportGroup?.subGroup || "",
          },
          {
            title: name || "",
            color: "#465869",
          },
        ];
      case EReportGroup.Management:
        return [
          {
            title: translate("reports"),
            link: {
              url: PRIVATE_ROUTES.ANALYTICS,
            },
          },
          {
            title: reportGroup?.group || "",
          },
          {
            title: name || "",
            color: "#465869",
          },
        ];
      default:
        return [
          {
            title: translate("reports"),
            link: {
              url: PRIVATE_ROUTES.ANALYTICS,
            },
          },
          {
            title: reportGroup?.group || "",
          },
          {
            title: reportGroup?.subGroup || "",
            color: "#465869",
          },
        ];
    }
  };

  return (
    <div>
      {!isMoving(currentUser?.company) && (
        <SubHeader showCompanyVersion>
          <Breadcrumb items={getBreadCrumbItems(reportGroup)} />
        </SubHeader>
      )}
      <MainDiv>
        {renderReportName()}
        <GridDiv>{chooseGridBySubgroup()}</GridDiv>
      </MainDiv>
      <GenerateReportModal ref={modalRef} jobs={jobs} report={{ name, reportId, checklistId }} />
    </div>
  );
};

export default ReportMaintenance;
